import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { queryKeys, useProjectOverviewQuery } from "@/api/hooks";
import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import type { CandidateSummary } from "@/api/preview";
import {
  classifyPublicationFailure,
  useCreatePublicationMutation,
  type PublicationFailure,
} from "@/api/publication";
import { useSession } from "@/auth/SessionContext";
import { formatDateTime } from "@/lib/format";
import { lifecycleRefusalCopy } from "@/lib/lifecycle";
import {
  clearIdempotencyKey,
  publicationRequestFor,
} from "@/publishing/publicationRequest";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { useToast } from "@/ui/Toast";
import { StaleDataNotice } from "@/ui/states";

/**
 * Submit for review — the route-backed canonical modal over the Project
 * Overview (#518).
 *
 * `/projects/:projectId/submit` renders this dialog over the Overview. It is
 * the canonical authority's submission surface: the accepted testing version,
 * the service-backed What changed summary, and one Submit action. Closing it —
 * Escape, the close control, Cancel, the overlay — navigates to the project
 * root, which is what makes the address real.
 *
 * NO PUBLISHING-ACCOUNT GATE. The canonical prototype showed the clinician's
 * delegated GitHub standing here with a connect action beside it. Publication
 * now runs as the environment's GitHub App installation, attributed to the
 * signed-in requester: session + Editor role is the whole precondition, so the
 * modal states nothing about a GitHub account, asks for no connection, and
 * offers no reconnect — the service's own publishing identity being
 * unavailable is stated as such, after the press, with the accepted version's
 * safety said first.
 *
 * WHAT SUBMISSION IS, in this repository's ratified chronology (#437): rung 1,
 * the publication request. The request body is the exact accepted candidate
 * tuple plus an idempotency key — nothing else; the service opens the review a
 * manager approves externally. The rung-4 `PublicationHandoffDialog` (note +
 * signed approval PDF, after approval and merge) is UNTOUCHED by this modal:
 * the two collect different evidence at different rungs, and the coexistence
 * is documented in the delivering pull request.
 *
 * NO MOCK SUBMISSION EVIDENCE. `CreatePublicationRequest` carries only the
 * accepted candidate tuple and idempotency key. The Lovable note/PDF controls
 * therefore do not render here: collecting and discarding them would be a
 * dead control and PREVIEW-09's mock attachment state, both explicitly outside
 * #651. Real release-team notes and signed approval evidence remain at rung 4,
 * where the service actually records them.
 *
 * GATING IS SERVER-AUTHORITATIVE, the `PublicationLane` pattern verbatim: the
 * exact current candidate must carry the qualifying acceptance
 * (`candidateAcceptance === "accepted"` AND the accepted id is the current
 * candidate's), and re-offers after a failure follow the service's own
 * `action_needed` reasons. The browser never widens what the service said.
 *
 * IDEMPOTENCY. The request body carries the key
 * `publishing/publicationRequest.ts` minted at the first request for this
 * candidate; the lane and this modal read the same key, so a retry from either
 * surface after a transient failure replays the SAME request, which the
 * contract answers with the original record rather than a second pull request.
 */

// `OFFERS_PUBLICATION` stood here — a `Record<string, …>` copy of the lane's
// policy table, "kept byte-identical" by comment alone, which is exactly the
// loosely-keyed duplicate the contracts README's enum-change checklist warns
// cannot fail compile when the enum moves. #651 retired both copies for
// `lifecycle.capabilities.requestPublication` (#650): the server's own
// per-candidate verdict, read from the same Overview response, so the modal
// and the lane cannot disagree because neither decides.

/**
 * The publication refusals, in `PublicationLane`'s sentences — the states are
 * the service's and the copy already ratified.
 */
const PUBLICATION_FAILURE_COPY = {
  not_evidence_eligible:
    "This version cannot be published. Freeze a new version once the approved safety-rule catalogue is in place.",
  head_moved:
    "This version moved since it was accepted. Review the content and accept it again before requesting publication.",
  idempotency_conflict:
    "A different publication was already requested with this attempt. Reload the page and try again.",
  publishing_unavailable:
    "The publishing service is unavailable. Your accepted version is safe; try again later or contact an administrator.",
  publishing_refused:
    "GitHub refused the publishing service's request. Your accepted version is safe; an administrator needs to check the publishing service's access before this can be retried.",
  denied: "You are not authorized to request publication for this Project.",
  unavailable:
    "Requesting publication is not available in this environment yet.",
  error: "Publication could not be requested. Try again.",
} satisfies Record<PublicationFailure["kind"], string>;

export function ProjectSubmitDialog({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { session } = useSession();

  const overview = useProjectOverviewQuery(projectId);
  const publication = useCreatePublicationMutation(projectId);

  const [failure, setFailure] = useState<PublicationFailure | null>(null);

  /**
   * Two-phase close, exactly as `ProjectPreviewDialog` explains it: the
   * dialog's close lifecycle (and its focus return) runs first, and the
   * navigation to the project root follows.
   */
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (!open) {
      void navigate(`/projects/${encodeURIComponent(projectId)}`);
    }
  }, [open, navigate, projectId]);
  function close() {
    setOpen(false);
  }

  const status = overview.data?.publicationStatus ?? null;
  const candidate: CandidateSummary | null =
    overview.data?.draft.latestCandidate ?? null;
  const lifecycle = overview.data?.lifecycle ?? null;
  const publishVerdict = lifecycle?.capabilities.requestPublication ?? null;
  const acceptVerdict = lifecycle?.capabilities.acceptCandidate ?? null;

  /**
   * Kept for the accepted-card copy below; the GATE is the verdict (#650).
   * The id comparison remains the #431 fail-closed pairing.
   */
  const currentCandidateAccepted =
    candidate !== null &&
    status !== null &&
    status.candidateAcceptance === "accepted" &&
    status.acceptedCandidateId === candidate.candidateId;
  /**
   * Submission is offered exactly where the server says the publication write
   * would be accepted — the lane's verdict, from the same Overview response,
   * so the modal and the lane cannot disagree because neither decides (#650).
   */
  const offersPublication = publishVerdict?.state === "permitted";

  /**
   * Why the submit control is withheld, stated. `null` means it is offered,
   * or that the acceptance gate notice below already carries the sentence.
   * The sentence is the server's stable refusal in its ratified copy — an
   * in-flight review, an already-published version, or missing authorization.
   */
  const submitBlockReason =
    !currentCandidateAccepted ||
    publishVerdict === null ||
    publishVerdict.state !== "refused"
      ? null
      : lifecycleRefusalCopy(publishVerdict.reason);

  /**
   * The server's revision number (#650). "The contract carries no version
   * number" was this surface's recorded justification for deriving N from a
   * list index over a second request; the contract now carries the number,
   * and the second request is gone with the derivation.
   */
  const ordinal = candidate?.revision?.number ?? null;

  const submitting = publication.isPending;
  const ready = offersPublication && !submitting && candidate !== null;

  function submit() {
    if (!ready || candidate === null) {
      return;
    }
    setFailure(null);

    // One POST with the session the browser already holds; the service
    // publishes as the environment's GitHub App and records this user as the
    // requester. The idempotency key is the one minted at the first request
    // for this candidate, so a retry replays rather than duplicates.
    const request = publicationRequestFor({
      projectId,
      candidate: {
        candidateId: candidate.candidateId,
        contentHash: candidate.contentHash,
        basePackSha: candidate.basePackSha,
      },
    });

    publication.mutate(request, {
      onSuccess() {
        clearIdempotencyKey(projectId, candidate.candidateId);
        toast("Submitted for review — waiting for a manager");
        close();
      },
      onError(error) {
        setFailure(classifyPublicationFailure(error));
      },
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submitting) {
          close();
        }
      }}
    >
      <DialogContent
        title="Submit for review"
        description="A manager reviews this before anything is published. Nothing goes live from here."
        busy={submitting}
        footer={
          <>
            <Button variant="outline" onClick={close} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={() => submit()}
              disabled={!ready}
              aria-describedby={
                submitBlockReason === null ? undefined : "submit-block-reason"
              }
            >
              {submitting ? "Submitting…" : "Submit for review"}
            </Button>
          </>
        }
      >
        {/*
          The two prerequisite reads gate everything below, so their OWN
          states are stated before anything is derived from them. An overview
          the service refused (403) or could not serve (503) is unknown, not
          "nothing accepted" — falling through to the acceptance-gate sentence
          would tell the clinician to redo work the service never disclaimed.
          Pending routes through `QueryPendingState` so a paused query (held
          retry) states itself instead of rendering a skeleton forever.
        */}
        {overview.isPending ? (
          <QueryPendingState
            isPaused={overview.isPaused}
            failureReason={overview.failureReason}
            label="Loading publication state"
            errorTitle="This project's publication state could not be loaded"
            rows={3}
            queryKey={queryKeys.projectOverview(projectId)}
          />
        ) : overview.data === undefined ? (
          <QueryErrorState
            error={overview.error}
            subject="Publication state"
            title="This project's publication state could not be loaded"
            description="Whether an accepted test version exists could not be read, so nothing here can say what submitting would send. Nothing was submitted."
            onRetry={() => void overview.refetch()}
          />
        ) : (
          <div className="space-y-4">
            {/* Cached data survives a failed refresh only under an explicit
                stale notice — silently current-looking gating would be the
                same false statement one step later. */}
            {overview.isError ? (
              <StaleDataNotice
                updatedAt={overview.dataUpdatedAt}
                onRetry={() => void overview.refetch()}
              />
            ) : null}
            {/* The accepted testing version, or the canonical gate sentence. */}
            {currentCandidateAccepted && candidate !== null ? (
              <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
                <p className="font-medium">
                  {ordinal === null
                    ? "Accepted test version"
                    : `Test version ${ordinal}`}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {`Created ${formatDateTime(candidate.createdAt)} · accepted after preview by ${
                    status?.acceptedBy === session.user.subject
                      ? "you"
                      : "another clinician"
                  }.`}
                </p>
              </div>
            ) : (
              <p
                role="status"
                className="rounded-md border border-warning-foreground/40 bg-warning/40 p-3 text-sm text-warning-foreground"
              >
                {/*
                  The expired arm names its own recovery (#650): previewing and
                  accepting THIS version is no longer possible, and sending a
                  clinician to do exactly that would be the misnamed recovery
                  the named refusal exists to prevent.
                */}
                {acceptVerdict?.state === "refused" &&
                (acceptVerdict.reason === "lifecycle.candidate_expired" ||
                  acceptVerdict.reason === "lifecycle.candidate_superseded")
                  ? lifecycleRefusalCopy(acceptVerdict.reason)
                  : "Preview this project as a patient and accept the preview first. Only an accepted test version can be sent for review."}
              </p>
            )}

            <div className="rounded-md border border-border bg-muted/30 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                What changed
              </p>
              {/*
                The Editor Service records no per-draft change list today, so
                the canonical section renders its honest empty state — never a
                fabricated changelog.
              */}
              <p className="text-sm text-muted-foreground">
                No changes recorded.
              </p>
            </div>

            {submitBlockReason === null ? null : (
              <p
                id="submit-block-reason"
                className="text-xs text-muted-foreground"
              >
                {submitBlockReason}
              </p>
            )}

            {failure === null ? null : (
              <p role="alert" className="text-sm text-foreground">
                {PUBLICATION_FAILURE_COPY[failure.kind]}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
